import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, Download, Gift, Copy } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";

export default function Account() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [referral, setReferral] = useState(null);

  useEffect(() => {
    api
      .get("/orders")
      .then((res) => setOrders(res.data))
      .finally(() => setLoading(false));
    api.get("/referral/me").then((res) => setReferral(res.data)).catch(() => {});
  }, []);

  const copyLink = () => {
    if (!referral) return;
    navigator.clipboard.writeText(referral.link);
    toast.success("Referral link copied!");
  };

  return (
    <div data-testid="account-page" className="mx-auto max-w-5xl px-5 py-12 md:px-8">
      <div className="flex flex-col justify-between gap-2 border-b border-obsidian/15 pb-8 md:flex-row md:items-end">
        <div>
          <span className="text-xs font-semibold uppercase tracking-[0.25em] text-obsidian/50">My account</span>
          <h1 className="mt-3 font-serif text-4xl font-light tracking-tighter text-obsidian md:text-5xl">
            Hi, {user?.name?.split(" ")[0] || "there"}
          </h1>
          <p className="mt-2 text-sm text-obsidian/60">{user?.email}</p>
        </div>
        {user?.role === "admin" && (
          <Link to="/admin" className="border border-obsidian px-6 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-obsidian transition-colors hover:bg-obsidian hover:text-white">
            Admin dashboard
          </Link>
        )}
      </div>

      {/* Refer & earn */}
      {referral && (
        <div data-testid="referral-section" className="mt-10 border border-obsidian/15 bg-obsidian p-6 text-white md:p-8">
          <div className="flex flex-col justify-between gap-6 md:flex-row md:items-center">
            <div>
              <div className="flex items-center gap-2 text-signal">
                <Gift className="h-5 w-5" />
                <span className="text-xs font-semibold uppercase tracking-[0.2em]">Refer & earn {referral.rate}%</span>
              </div>
              <h3 className="mt-3 font-serif text-2xl font-light tracking-tight">Share LuxDrop, earn on every sale</h3>
              <p className="mt-1 max-w-md text-sm text-white/55">
                Send your link to fellow freelancers. When they buy, you earn {referral.rate}% commission.
              </p>
              <div className="mt-4 flex max-w-md items-center gap-2">
                <input
                  data-testid="referral-link"
                  readOnly
                  value={referral.link}
                  className="min-w-0 flex-1 border border-white/20 bg-transparent px-3 py-2.5 text-sm text-white/80 focus:outline-none"
                />
                <button
                  data-testid="referral-copy"
                  onClick={copyLink}
                  className="inline-flex shrink-0 items-center gap-2 bg-signal px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-white transition-colors hover:bg-red-700"
                >
                  <Copy className="h-4 w-4" /> Copy
                </button>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-px bg-white/10 md:w-80">
              {[
                { label: "Referrals", value: referral.referred_orders },
                { label: "Sales", value: `$${referral.total_sales.toFixed(0)}` },
                { label: "Earned", value: `$${referral.commission.toFixed(2)}` },
              ].map((s) => (
                <div key={s.label} className="bg-obsidian p-4 text-center">
                  <p className="font-serif text-2xl tracking-tighter">{s.value}</p>
                  <p className="mt-1 text-[10px] uppercase tracking-[0.15em] text-white/40">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <h2 className="mt-10 text-xs font-semibold uppercase tracking-[0.2em] text-obsidian/50">Order history</h2>

      {loading ? (
        <div className="mt-6 space-y-3">
          {[...Array(3)].map((_, i) => <div key={i} className="h-24 animate-pulse bg-secondary" />)}
        </div>
      ) : orders.length === 0 ? (
        <div className="mt-6 border border-obsidian/15 bg-white p-12 text-center">
          <Package className="mx-auto h-10 w-10 text-obsidian/30" />
          <p className="mt-4 text-obsidian/60">No orders yet.</p>
          <Link to="/" className="mt-4 inline-block text-signal underline">Start shopping</Link>
        </div>
      ) : (
        <div className="mt-6 space-y-4">
          {orders.map((order) => (
            <div key={order.id} data-testid={`order-${order.id}`} className="border border-obsidian/15 bg-white">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-obsidian/10 p-5">
                <div>
                  <p className="font-serif text-xl tracking-tighter text-obsidian">{order.order_number}</p>
                  <p className="text-xs uppercase tracking-[0.15em] text-obsidian/40">
                    {new Date(order.created_at).toLocaleDateString()} · {order.status}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-serif text-2xl tracking-tighter text-obsidian">${order.total.toFixed(2)}</p>
                </div>
              </div>
              <div className="divide-y divide-obsidian/5">
                {order.items.map((it) => (
                  <div key={it.product_id} className="flex items-center justify-between p-4">
                    <span className="text-sm text-obsidian/80">{it.title} × {it.quantity}</span>
                    <a
                      href={`${API}/orders/${order.id}/download/${it.product_id}`}
                      data-testid={`download-${it.product_id}`}
                      className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-signal hover:underline"
                    >
                      <Download className="h-4 w-4" /> Download
                    </a>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
